"use client";
import isEmpty from "lodash/isEmpty";
import { useMemo, useState, useEffect } from "react";

import useFetchStadiumPoints, {
  useFetchWorkspacePoints,
} from "@stadium/checkout/src/hooks/use-fetch-stadium-points";

import { CheckoutContext, useCheckoutContext } from "@stadium/checkout/src/contexts";

import usePrevious from "@stadium/hooks/lib/use-previous";
import { useConfig } from "@stadium/hooks/lib/use-config";
import useUpdateOrder from "@stadium/checkout/src/hooks/use-update-order";
import { useFeatureFlag, useFlags } from "@stadium/services/launch-darkly";
import { useWorkspaceContext } from "@stadium/hooks/lib/workspace";

import {
  getConversionRates,
  getCurrencies,
  getPompFeeInUsd,
} from "@stadium/redux-utils/selectors/configSelector";

import { formatNumber } from "@stadium/helpers/number";
import { useAuth0V2 } from "@stadium/services/auth0";

import { AUTHORIZED_PAYMENT_OPTIONS } from "@stadium/constants/wallets";
import {
  PAYMENT_METHODS,
  DEFAULT_SINGLE_SOURCE_PAYMENT_METHODS,
} from "@stadium/checkout/src/constants/payment";
import { DEFAULT_CURRENCY_CODE, MY_WORKSPACE_SLUG } from "@stadium/constants";
import { TREAT_ORDER_RESPONSE_TYPE } from "@stadium/checkout/src/constants/order";
import {
  workspaceWalletFlag,
  workspaceLaunchFlag,
} from "@stadium/services/launch-darkly/constants";

export { useCheckoutContext };

const emptyObj = {};
const emptyFn = () => {};

const { stadiumWalletKey, stadiumPointsWalletKey, storeSpecificPointsKey } =
  AUTHORIZED_PAYMENT_OPTIONS;

const CheckoutProvider = ({
  children,
  currentUserData,
  singleSourcePaymentMethods = DEFAULT_SINGLE_SOURCE_PAYMENT_METHODS,
  storeSpecificBalance = 0,
  orderData: initOrderData = {},
  shouldUpdateTreat = false,
  postOrderUpdateCallback = () => emptyObj,
  orderResponseType = "",
  customOrderUpdateFn = (_, callback) => callback?.(),
  customOrderUpdateFnLoading = false,
  additionalOrderParams = {},
  workspaceSlug: workspaceSlugFromProps,
  isSelfOrder = false,
}) => {
  const { isReady } = useFeatureFlag();
  const {
    [workspaceWalletFlag]: workspaceWalletFlagEnabled,
    [workspaceLaunchFlag]: workspaceLaunchFlagEnabled,
  } = useFlags();

  const { workspaceSlug: workspaceSlugFromDashboard } = useWorkspaceContext();

  const workspaceSlug = workspaceSlugFromProps || workspaceSlugFromDashboard || MY_WORKSPACE_SLUG;
  const isPersonalWorkspace = workspaceSlug === MY_WORKSPACE_SLUG;

  const { isAuthenticated } = useAuth0V2();
  const currencies = useConfig(getCurrencies);
  const conversionRates = useConfig(getConversionRates);
  const pompFee = useConfig(getPompFeeInUsd);
  const nonSingleSourcePaymentMethods = Object.values(PAYMENT_METHODS)
    .map((paymentMethod) => paymentMethod.key)
    .filter((paymentMethod) => !singleSourcePaymentMethods.includes(paymentMethod));
  const { corporateBilled: isCorporateBilledEnabled, walletCurrency = {} } = currentUserData;

  const isLocalCartOrder = initOrderData?.isLocalCartOrder || !isAuthenticated;

  const [selectedTeamWallet, setSelectedTeamWallet] = useState("");
  const [isTeamWalletSelectedByDefault, setIsTeamWalletSelectedByDefault] = useState(false);
  const [billingType, setBillingType] = useState(null);
  const [pompSelected, setPompSelected] = useState(null);

  const [selectedAddress, setSelectedAddress] = useState(emptyObj);
  const [submittedAddress, setSubmittedAddress] = useState(null);

  const [orderData, setOrderData] = useState(emptyObj);
  const [checkoutStepsState, setCheckoutStepsState] = useState(emptyObj);
  const [currentCheckoutStep, setCurrentCheckoutStep] = useState("");

  const {
    useGlobalPoint: useStadiumPoints,
    useStoreSpecificPoint: useStoreSpecificPoints,
    useWalletMoney: useWallet,
    creditCardId,
    teamId,
    authorizedPayments,
    billedCurrencyId,
    corporateBilled: isCorporateBilledDefaultSelected,
  } = orderData;

  const orderCurrencyCode = currencies.find((currency) => currency.id === billedCurrencyId);

  const currency = useMemo(() => {
    if (orderCurrencyCode) return orderCurrencyCode;
    if (walletCurrency?.code) return walletCurrency;
    return DEFAULT_CURRENCY_CODE;
  }, [orderCurrencyCode, walletCurrency]);

  const isPaymentPending = !isEmpty(authorizedPayments);

  const [billingCountry, setBillingCountry] = useState(emptyObj);
  const [billingZipCode, setBillingZipCode] = useState("");
  const [billingNote, setBillingNote] = useState("");
  const [billingTaxNumber, setBillingTaxNumber] = useState("");

  const giftDetailAttributes = useMemo(() => {
    if (orderResponseType === TREAT_ORDER_RESPONSE_TYPE && initOrderData?.giftDetail?.id) {
      return {
        gift_detail_attributes: { id: initOrderData?.giftDetail?.id, fe_for_eshop: true },
      };
    }

    return {};
  }, [initOrderData]);

  const { updateOrder: updateOrderFn, loading: isUpdatingOrder } = useUpdateOrder({
    orderId: orderData?.id,
    orderNumber: orderData?.number,
    orderResponseType,
    shouldUpdateTreat,
    giftDetailAttributes,
    additionalOrderParams,
    setOrderData: (order) => {
      if (isSelfOrder) {
        setOrderData({ ...orderData, ...order });
        setPompSelected(order?.hasPomp);
      }
    },
  });

  const updateOrder = isLocalCartOrder ? customOrderUpdateFn : updateOrderFn;

  const conversionRate = useMemo(
    () => conversionRates[currency?.code?.toLowerCase()],
    [conversionRates, currency],
  );

  const prevConversionRate = usePrevious(conversionRate);

  const { rewardPoints: newStadiumPoints, refetch: refetchNewStadiumPoints } =
    useFetchWorkspacePoints({
      skip: !isAuthenticated || !isReady || !workspaceLaunchFlagEnabled,
      workspaceSlug,
    });

  const { rewardPoints: oldStadiumPoints, refetch: refetchOldStadiumPoints } =
    useFetchStadiumPoints({
      skip: !isAuthenticated || isEmpty(currentUserData) || !isReady || workspaceLaunchFlagEnabled,
    });

  const stadiumPoints = workspaceLaunchFlagEnabled ? newStadiumPoints : oldStadiumPoints;
  const refetchStadiumPoints = workspaceLaunchFlagEnabled
    ? refetchNewStadiumPoints
    : refetchOldStadiumPoints;

  const isSingleSourcePaymentMethodSelected =
    singleSourcePaymentMethods.includes(billingType) || selectedTeamWallet?.id;

  useEffect(() => {
    if (!orderData?.id && initOrderData?.id) {
      setOrderData(initOrderData);
      setPompSelected(initOrderData?.hasPomp);
    }

    if (initOrderData?.isLocalCartOrder && isEmpty(orderData)) {
      setOrderData(initOrderData);
    }
  }, [initOrderData]);

  // adding already paid amount to points for payment failure cases.
  const stadiumPointsBalance = useMemo(() => {
    const stadiumPointsPaymentObj = authorizedPayments?.[stadiumPointsWalletKey];
    if (stadiumPointsPaymentObj) {
      return stadiumPoints + +stadiumPointsPaymentObj.originalAmount;
    }
    return stadiumPoints;
  }, [stadiumPoints, authorizedPayments, currency]);

  // adding already paid amount to wallet for payment failure cases.
  const userWalletBalance = useMemo(() => {
    if (workspaceSlug !== MY_WORKSPACE_SLUG && workspaceWalletFlagEnabled) return 0;
    const originalWalletBalance = parseFloat(currentUserData?.walletBalance);
    const stadiumWalletPaymentObj = authorizedPayments?.[stadiumWalletKey];
    if (stadiumWalletPaymentObj) {
      return originalWalletBalance + +stadiumWalletPaymentObj.originalAmount;
    }
    return originalWalletBalance;
  }, [
    currentUserData,
    authorizedPayments,
    currency,
    workspaceWalletFlagEnabled,
    workspaceSlug,
    isReady,
  ]);

  const selectedTeamWalletLabel = `Wallet: ${selectedTeamWallet?.name}`;

  const storeSpecificPointsBalance = useMemo(() => {
    const storeSpecificPointsPaymentObj = authorizedPayments?.[storeSpecificPointsKey];
    if (storeSpecificPointsPaymentObj) {
      return +storeSpecificBalance + +storeSpecificPointsPaymentObj.originalAmount;
    }
    return +storeSpecificBalance;
  }, [storeSpecificBalance, authorizedPayments, currency]);

  const formattedStadiumPointsBalance = `${formatNumber(stadiumPointsBalance)} Pts`;
  const canPayNow = useMemo(
    () =>
      (!currentCheckoutStep || checkoutStepsState[currentCheckoutStep]?.optional) &&
      Object.values(checkoutStepsState).every((step) => step.submitted || step.optional),
    [checkoutStepsState],
  );

  const handlePaymentMethodChange = ({ checked, paymentKey, additionalPayload = {} }) => {
    let paymentMethodsToReset = { [paymentKey]: checked, ...additionalPayload };

    if (checked) {
      const isSingleSourcePayment = singleSourcePaymentMethods.includes(
        PAYMENT_METHODS[paymentKey].key,
      );
      const paymentMethods = isSingleSourcePayment
        ? [PAYMENT_METHODS[paymentKey].key]
        : nonSingleSourcePaymentMethods;

      paymentMethodsToReset = {
        ...paymentMethodsToReset,
        ...resetAllPaymentMethodsExcept(paymentMethods),
      };
    }

    setOrderData({
      ...orderData,
      ...paymentMethodsToReset,
    });

    updateOrder(paymentMethodsToReset, postOrderUpdateCallback);
  };

  const setUseStadiumPoints = (checked) =>
    handlePaymentMethodChange({
      checked,
      paymentKey: PAYMENT_METHODS.useGlobalPoint.key,
    });

  const setUseWallet = (checked) =>
    handlePaymentMethodChange({
      checked,
      paymentKey: PAYMENT_METHODS.useWalletMoney.key,
    });

  const setUseStoreSpecificPoints = (checked) =>
    handlePaymentMethodChange({
      checked,
      paymentKey: PAYMENT_METHODS.useStoreSpecificPoint.key,
    });

  const setCurrency = (currency) => {
    if (billedCurrencyId !== currency.id) {
      setOrderData({
        ...orderData,
        billedCurrencyId: currency.id,
      });

      updateOrder(
        {
          billedCurrencyId: currency.id,
        },
        postOrderUpdateCallback,
      );
    }
  };

  const handleTeamWalletChange = (wallet) => {
    setSelectedTeamWallet(wallet);
    if ((isEmpty(wallet) && !teamId) || teamId === wallet?.id) {
      return;
    }

    handlePaymentMethodChange({
      checked: !!wallet,
      paymentKey: PAYMENT_METHODS.useTeamPoint.key,
      additionalPayload: {
        teamId: wallet?.id,
      },
    });
  };

  const setCreditCardId = (creditCardId) => {
    setBillingType(PAYMENT_METHODS.creditCardId.key);

    handlePaymentMethodChange({
      checked: creditCardId,
      paymentKey: PAYMENT_METHODS.creditCardId.key,
    });
  };

  const updateBillingType = (value) => {
    setBillingType(value);

    if (value === PAYMENT_METHODS.corporateBilled.key) {
      handlePaymentMethodChange({
        checked: true,
        paymentKey: PAYMENT_METHODS.corporateBilled.key,
      });
    }
  };

  const updateCheckoutStepsState = (stepKey) =>
    setCheckoutStepsState((prev) => ({
      ...prev,
      [stepKey]: { ...prev[stepKey], submitted: true },
    }));

  const resetAllPaymentMethodsExcept = (paymentMethods) => {
    const paymentMethodsResetPayload = {};

    if (!paymentMethods.includes(PAYMENT_METHODS.creditCardId.key)) {
      if (billingType === PAYMENT_METHODS.creditCardId.key) {
        setBillingType("");
      }
      paymentMethodsResetPayload[PAYMENT_METHODS.creditCardId.key] = null;
    }
    if (!paymentMethods.includes(PAYMENT_METHODS.newCreditCard.key)) {
      if (billingType === PAYMENT_METHODS.newCreditCard.key) {
        setBillingType("");
      }
    }
    if (!paymentMethods.includes(PAYMENT_METHODS.corporateBilled.key)) {
      if (billingType === PAYMENT_METHODS.corporateBilled.key) {
        setBillingType("");
        paymentMethodsResetPayload[PAYMENT_METHODS.corporateBilled.key] = false;
      }
    }
    if (!paymentMethods.includes(PAYMENT_METHODS.useGlobalPoint.key)) {
      paymentMethodsResetPayload[PAYMENT_METHODS.useGlobalPoint.key] = false;
    }
    if (!paymentMethods.includes(PAYMENT_METHODS.useStoreSpecificPoint.key)) {
      paymentMethodsResetPayload[PAYMENT_METHODS.useStoreSpecificPoint.key] = false;
    }
    if (!paymentMethods.includes(PAYMENT_METHODS.useWalletMoney.key)) {
      paymentMethodsResetPayload[PAYMENT_METHODS.useWalletMoney.key] = false;
    }
    if (!paymentMethods.includes(PAYMENT_METHODS.useTeamPoint.key)) {
      setSelectedTeamWallet(null);
      paymentMethodsResetPayload[PAYMENT_METHODS.useTeamPoint.key] = false;
      paymentMethodsResetPayload.teamId = null;
    }

    return paymentMethodsResetPayload;
  };

  useEffect(() => {
    if (billingType === PAYMENT_METHODS.corporateBilled.key) {
      if (singleSourcePaymentMethods.includes(PAYMENT_METHODS.corporateBilled.key)) {
        resetAllPaymentMethodsExcept([PAYMENT_METHODS.corporateBilled.key]);
      } else {
        resetAllPaymentMethodsExcept(nonSingleSourcePaymentMethods);
      }
    }

    if (billingType === PAYMENT_METHODS.creditCardId.key) {
      if (singleSourcePaymentMethods.includes(PAYMENT_METHODS.creditCardId.key)) {
        resetAllPaymentMethodsExcept([PAYMENT_METHODS.creditCardId.key]);
      } else {
        resetAllPaymentMethodsExcept(nonSingleSourcePaymentMethods);
      }
    }

    if (billingType === PAYMENT_METHODS.newCreditCard.key) {
      if (singleSourcePaymentMethods.includes(PAYMENT_METHODS.newCreditCard.key)) {
        resetAllPaymentMethodsExcept([PAYMENT_METHODS.newCreditCard.key]);
      } else {
        resetAllPaymentMethodsExcept(nonSingleSourcePaymentMethods);
      }
    }
  }, [billingType]);

  useEffect(() => {
    if (useStadiumPoints) {
      if (singleSourcePaymentMethods.includes(PAYMENT_METHODS.useGlobalPoint.key)) {
        resetAllPaymentMethodsExcept([PAYMENT_METHODS.useGlobalPoint.key]);
      } else {
        resetAllPaymentMethodsExcept(nonSingleSourcePaymentMethods);
      }
    }
  }, [useStadiumPoints]);

  useEffect(() => {
    if (useStoreSpecificPoints) {
      if (singleSourcePaymentMethods.includes(PAYMENT_METHODS.useStoreSpecificPoint.key)) {
        resetAllPaymentMethodsExcept([PAYMENT_METHODS.useStoreSpecificPoint.key]);
      } else {
        resetAllPaymentMethodsExcept(nonSingleSourcePaymentMethods);
      }
    }
  }, [useStoreSpecificPoints]);

  useEffect(() => {
    if (useWallet) {
      if (singleSourcePaymentMethods.includes(PAYMENT_METHODS.useWalletMoney.key)) {
        resetAllPaymentMethodsExcept([PAYMENT_METHODS.useWalletMoney.key]);
      } else {
        resetAllPaymentMethodsExcept(nonSingleSourcePaymentMethods);
      }
    }
  }, [useWallet]);

  useEffect(() => {
    if (selectedTeamWallet) {
      if (singleSourcePaymentMethods.includes(PAYMENT_METHODS.useTeamPoint.key)) {
        resetAllPaymentMethodsExcept([PAYMENT_METHODS.useTeamPoint.key]);
      } else {
        resetAllPaymentMethodsExcept(nonSingleSourcePaymentMethods);
      }
    }
  }, [selectedTeamWallet]);

  useEffect(() => {
    if (isCorporateBilledDefaultSelected && (isCorporateBilledEnabled || shouldUpdateTreat)) {
      setBillingType(PAYMENT_METHODS.corporateBilled.key);
    }
  }, [isCorporateBilledDefaultSelected, isCorporateBilledEnabled, shouldUpdateTreat]);

  const values = useMemo(
    () => ({
      useStadiumPoints,
      setUseStadiumPoints,
      useStoreSpecificPoints,
      setUseStoreSpecificPoints,
      stadiumPointsBalance,
      userWalletBalance,
      walletCurrency,
      useWallet,
      setUseWallet,
      selectedTeamWallet,
      setSelectedTeamWallet: handleTeamWalletChange,
      isPaymentPending,
      isCorporateBilledEnabled,
      creditCardId,
      setCreditCardId,
      billingType,
      setBillingType: updateBillingType,
      billingCountry,
      setBillingCountry,
      billingZipCode,
      setBillingZipCode,
      billingNote,
      setBillingNote,
      currencies,
      conversionRates,
      currency,
      setCurrency,
      conversionRate,
      prevConversionRate,
      formattedStadiumPointsBalance,
      storeSpecificPointsBalance,
      selectedAddress,
      setSelectedAddress,
      isSingleSourcePaymentMethodSelected,
      isTeamWalletSelectedByDefault,
      setIsTeamWalletSelectedByDefault,
      refetchStadiumPoints,
      billingTaxNumber,
      setBillingTaxNumber,
      pompSelected,
      setPompSelected,
      pompFee,
      orderData,
      setOrderData,
      updateCheckoutStepsState,
      setCheckoutStepsState,
      checkoutStepsState,
      currentCheckoutStep,
      setCurrentCheckoutStep,
      canPayNow,
      initOrderData,
      updateOrder,
      isUpdatingOrder: customOrderUpdateFnLoading || isUpdatingOrder,
      postOrderUpdateCallback,
      submittedAddress,
      setSubmittedAddress,
      selectedTeamWalletLabel,
      isPersonalWorkspace,
    }),
    [
      useStadiumPoints,
      setUseStadiumPoints,
      useStoreSpecificPoints,
      setUseStoreSpecificPoints,
      stadiumPointsBalance,
      userWalletBalance,
      walletCurrency,
      useWallet,
      setUseWallet,
      selectedTeamWallet,
      setSelectedTeamWallet,
      isPaymentPending,
      isCorporateBilledEnabled,
      creditCardId,
      setCreditCardId,
      billingType,
      setBillingType,
      billingCountry,
      setBillingCountry,
      billingZipCode,
      setBillingZipCode,
      billingNote,
      setBillingNote,
      currencies,
      conversionRates,
      currency,
      setCurrency,
      conversionRate,
      prevConversionRate,
      formattedStadiumPointsBalance,
      storeSpecificPointsBalance,
      selectedAddress,
      setSelectedAddress,
      isSingleSourcePaymentMethodSelected,
      isTeamWalletSelectedByDefault,
      setIsTeamWalletSelectedByDefault,
      refetchStadiumPoints,
      billingTaxNumber,
      setBillingTaxNumber,
      pompSelected,
      setPompSelected,
      pompFee,
      orderData,
      setOrderData,
      updateCheckoutStepsState,
      setCheckoutStepsState,
      checkoutStepsState,
      currentCheckoutStep,
      setCurrentCheckoutStep,
      canPayNow,
      initOrderData,
      updateOrder,
      isUpdatingOrder,
      postOrderUpdateCallback,
      customOrderUpdateFnLoading,
      submittedAddress,
      setSubmittedAddress,
      isPersonalWorkspace,
    ],
  );

  return <CheckoutContext.Provider value={values}>{children}</CheckoutContext.Provider>;
};

export default CheckoutProvider;
